import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import inReviewStatus from "../../../assets/visa-status/in-review.png";
import issuedStatus from "../../../assets/visa-status/issued.png";
import rejectedStatus from "../../../assets/visa-status/rejected.png";
import paymentsImg from "../../../assets/visa-status/payment-method.png";

import { InfoRow } from "../Payment/InfoRow";
import { path } from "../../../utils/common/constant";
import { Checkbox } from "antd";
import { Info } from "lucide-react";
import { useTranslation } from "react-i18next";
import visaRequestServices from "../../../api/services/visaRequestServices";
import dayjs from "dayjs";

interface RequestInfoField {
  label: string;
  value: string;
  editable?: boolean;
}

const ProfileResult = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [visaStatus, setVisaStatus] = useState("");
  const [currentVisaRequest, setCurrentVisaRequest] = useState<IVisaRequest>();

  const requestInfo: RequestInfoField[] = [
    {
      label: t("profile_search_result.request_created_date"),
      value: currentVisaRequest?.create_date
        ? dayjs(currentVisaRequest?.create_date).format("DD/MM/YYYY")
        : ""
    },
    {
      label: t("profile_search_result.request_last_update"),
      value: currentVisaRequest?.create_date
        ? dayjs(currentVisaRequest?.create_date).format("DD/MM/YYYY")
        : ""
    }
  ];

  const requestDetails: RequestInfoField[] = [
    {
      label: t("registration_form.step4.emergency_fullname"),
      value:
        (currentVisaRequest?.middle_and_given_name || "") +
        " " +
        (currentVisaRequest?.surname || "")
    },
    {
      label: t("registration_form.step3.passport_number"),
      value: currentVisaRequest?.request_passport_number || ""
    },
    {
      label: t("payment_details.visa_code"),
      value: "VN983258235308"
    },
    {
      label: t("payment_details.passport_type"),
      value:
        currentVisaRequest?.passport_type === "Ordinary"
          ? t("registration_form.step3.ordinary_passport")
          : currentVisaRequest?.passport_type === "Diplomatic"
          ? t("registration_form.step3.diplomatic_passport")
          : currentVisaRequest?.passport_type === "Official"
          ? t("registration_form.step3.official_passport")
          : t("registration_form.step3.other_passport")
    },
    {
      label: t("registration_form.step2.date_of_birth"),
      value: currentVisaRequest?.date_of_birth
        ? dayjs(currentVisaRequest?.date_of_birth).format("DD/MM/YYYY")
        : ""
    },
    {
      label: t("registration_form.step2.email"),
      value: currentVisaRequest?.email || ""
    },
    {
      label: t("registration_form.step2.nationality"),
      value: currentVisaRequest?.nationality || ""
    },
    {
      label: t("payment_details.to_issue_visa_for"),
      value: currentVisaRequest?.evisa_entry_type || ""
    },
    {
      label: t("payment_details.date_of_entry"),
      value: currentVisaRequest?.intended_date_of_entry
        ? dayjs(currentVisaRequest?.intended_date_of_entry).format("DD/MM/YYYY")
        : ""
    },
    {
      label: t("profile_search_result.to_date"),
      value: currentVisaRequest?.intended_date_of_entry
        ? dayjs(currentVisaRequest?.intended_date_of_entry)
            .add(currentVisaRequest?.intended_length_of_stay || 0, "days")
            .format("DD/MM/YYYY")
        : ""
    }
  ];

  useEffect(() => {
    const getVisaRequest = async (id: number) => {
      const response = await visaRequestServices.getVisaRequestById(
        id.toString()
      );

      if (response.error) {
        console.log(response.error);
        return;
      }

      setCurrentVisaRequest(response.data);
    };

    if (location.state) {
      getVisaRequest(location.state.request_id);
      setVisaStatus(location.state.request_status);
    }
  }, [location]);

  return (
    <div className="flex flex-col">
      <div className="w-full py-8 px-4 text-center text-white bg-gradient-to-b from-[#F00D0D] to-[#994F4F]">
        <h1 className="text-[32px] font-bold mb-2">
          {t("profile_search.title")}
        </h1>
        <p className="text-[22px] font-bold">
          {t("profile_search.description")}
        </p>
      </div>

      <div className="w-full px-6 py-10 flex flex-col items-center justify-center">
        <div className="w-full max-w-[1290px] flex flex-col justify-center items-center gap-8">
          <div className="flex flex-col gap-2 text-black lg:text-3xl text-2xl text-center">
            <p className="font-medium">{t("profile_search_result.ministry")}</p>

            <p className="font-bold">
              {t("profile_search_result.ministry_title")}
            </p>
          </div>

          <div className="w-full bg-[#8BBDEA]/20 px-8 py-2 justify-center rounded-xl">
            <div className="text-[#074799] text-left font-bold text-base">
              {t("visa_requirements_details.hint")}
            </div>
          </div>

          {/* Icon Section */}
          <div className="w-full flex flex-col justify-center items-center py-10 gap-8">
            <p
              className="font-bold lg:text-[50px] text-4xl"
              style={{
                color:
                  visaStatus === "Approve"
                    ? "#34c759"
                    : visaStatus === "Deny"
                    ? "#EC1C24"
                    : "#074799"
              }}
            >
              {visaStatus === "Pending"
                ? t("profile_search_result.under_review")
                : visaStatus === "Approve"
                ? t("profile_search_result.issued")
                : t("profile_search_result.rejected")}
            </p>

            <img
              src={
                visaStatus === "Approve"
                  ? issuedStatus
                  : visaStatus === "Deny"
                  ? rejectedStatus
                  : inReviewStatus
              }
              alt="status-icon"
            />

            <p className="text-3xl font-medium">
              {visaStatus === "Approve"
                ? t("profile_search_result.issued.title")
                : visaStatus === "Deny"
                ? t("profile_search_result.rejected.title")
                : t("profile_search_result.under_review.title")}
            </p>

            <p className="text-xl text-[#838383] font-medium text-center">
              {visaStatus === "Approve"
                ? t("profile_search_result.issued.description")
                : visaStatus === "Deny"
                ? t("profile_search_result.rejected.description")
                : t("profile_search_result.under_review.description")}
            </p>
          </div>

          {/* Request Info */}
          <div className="w-full grid md:grid-cols-2">
            {requestInfo.map((info, index) => (
              <InfoRow key={index} label={info.label} value={info.value} />
            ))}

            <div className="border-b border-[#E3DBD8] py-4">
              <div className="grid grid-cols-12 justify-between">
                <span className="text-base text-gray-600 col-span-4">
                  {t("registration_form.step3.visa_type")}
                </span>
                <span className="text-base text-[#074799] font-bold ml-2 col-span-7">
                  {currentVisaRequest?.evisa_entry_type || ""}
                </span>
              </div>
            </div>

            <div className="border-b border-[#E3DBD8] py-4">
              <div className="grid grid-cols-12 justify-between">
                <span className="text-base text-gray-600 col-span-4">
                  {t("home.processing_plan.title")}
                </span>
                <span className="text-base text-[#34c759] font-bold ml-2 col-span-7">
                  {t("home.processing_plan.plan2.title")}
                </span>
              </div>
            </div>
          </div>

          {/* Reject Reason - Only For Deny */}
          {visaStatus === "Deny" && (
            <div className="w-full flex flex-col">
              <p className="text-3xl font-semibold mb-4">
                {t("profile_search_result.response")}
              </p>

              <p className="mb-2 text-primary-blue">
                {t("profile_search_result.reject_info")}
              </p>

              <div className="flex justify-center items-center p-2 border border-primary-blue rounded-lg">
                <p className="text-justify">
                  You have been notified that your registration for an immigrant
                  visa was rejected, and any petition approved on your behalf
                  was also rejected. We informed you that your application might
                  be reinstated if, within one year, you could establish that
                  your failure to pursue your immigrant visa application was due
                  to circumstances beyond your control. Since you have failed to
                  do so, the record of your registration and any petition
                  approved on your behalf and all supporting documents have been
                  destroyed; any Department of Labor certification has been
                  returned to your registration place.
                </p>
              </div>
            </div>
          )}

          {/* Request Detail */}
          <div className="w-full flex flex-col gap-4">
            <p className="text-3xl font-semibold">
              {t("profile_search_result.request_details")}
            </p>
            <div className="w-full grid md:grid-cols-2">
              {requestDetails.map((info, index) => (
                <InfoRow
                  key={index}
                  label={info.label}
                  value={info.value}
                  editable={visaStatus === "Deny" ? true : false}
                />
              ))}
            </div>
          </div>

          {visaStatus === "Deny" && (
            <div className="w-full flex flex-col gap-8">
              <div className="grid md:grid-cols-4 max-md:space-y-4 bg-[#8BBDEA]/20 p-4 justify-center rounded-lg items-center">
                <div className="col-span-1 text-primary-blue text-center font-semibold">
                  {t("payment_details.content_billing")}
                </div>
                <div className="col-span-1 text-primary-blue text-center font-semibold text-[22px]">
                  {t("payment_details.visa_fee")}
                </div>
                <div className="col-span-1 text-primary-blue text-center font-semibold">
                  {t("payment_details.amount")}
                </div>
                <div className="col-span-1 text-primary-blue text-center font-semibold text-[22px]">
                  39.50$
                </div>
              </div>

              <div className="text-center text-3xl font-semibold text-primary-blue">
                {t("payment_details.payment_methods")}
              </div>

              <div className="flex justify-center items-center">
                <img src={paymentsImg} alt="payment-method" />
              </div>

              {/* Payment Policy Section */}
              <div className="mt-8">
                <h2 className="font-bold text-primary-blue mb-4 flex items-center gap-2">
                  <Info className="w-5 h-5 text-primary-blue" />
                  {t("payment_details.payment_policy")}
                </h2>
                <div className="space-y-4">
                  <p className="text-primary-blue leading-relaxed">
                    {t("payment_details.payment_policy1")}
                  </p>
                  <p className="text-primary-blue leading-relaxed">
                    {t("payment_details.payment_policy2")}
                  </p>
                </div>

                {/* Checkbox Agreement */}
                <div className="mt-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox>
                      <span className="text-primary-blue text-base">
                        {t("payment_details.agree_policy")}
                      </span>
                    </Checkbox>
                  </label>
                </div>
              </div>
            </div>
          )}

          <div className="flex w-full justify-center items-center">
            <div className="flex max-md:flex-col gap-4 mt-8 w-full justify-center items-center text-nowrap">
              {visaStatus !== "Pending" && (
                <button
                  className="outline-none w-56 py-6 border font-bold border-primary-blue rounded-full text-primary-blue"
                  onClick={() => {
                    if (visaStatus === "Approve") {
                      navigate("../" + path.VIEW_VISA);
                    } else {
                      navigate("../" + path.PROFILE_SEARCH);
                    }
                  }}
                >
                  {visaStatus === "Approve"
                    ? t("profile_search_result.view_visa")
                    : t("registration_form.cancel")}
                </button>
              )}
              <button
                className="outline-none w-56 py-6 border font-bold border-transparent rounded-full text-primary-blue bg-primary-lightyellow border-primary-lightyellow"
                onClick={() => {
                  if (visaStatus === "Deny") {
                    navigate("../" + path.PROFILE_SEARCH);
                  } else {
                    navigate("../" + path.HOME);
                  }
                }}
              >
                {visaStatus === "Deny"
                  ? t("registration_form.continue")
                  : t("cta.back_to_home")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileResult;
